"use client"

import { useMemo, useState } from "react"
import { X } from "lucide-react"

// Known terpenes with color + aroma + a short description, matching the kind
// of info Dutchie surfaces when you tap a terpene. Falls back to gray/no-desc
// for unknowns so they still render.
interface TerpMeta { color: string; aroma: string; desc?: string }
const TERP_META: Record<string, TerpMeta> = {
  myrcene:        { color: "#6b8e23", aroma: "Earthy, musky",     desc: "The most common cannabis terpene. Earthy and clove-like, it's associated with relaxing, couch-lock effects." },
  limonene:       { color: "#f4b400", aroma: "Citrus, uplifting", desc: "Bright citrus aroma found in citrus rinds. Often linked to uplifted mood and stress relief." },
  caryophyllene:  { color: "#b45309", aroma: "Peppery, spicy",    desc: "Spicy and peppery, also found in black pepper and cloves. The only terpene that binds cannabinoid receptors; associated with calm." },
  pinene:         { color: "#2e7d32", aroma: "Pine, focus",       desc: "Sharp pine scent shared with pine needles and rosemary. Associated with alertness and focus." },
  linalool:       { color: "#8e44ad", aroma: "Floral, calm",      desc: "Floral lavender aroma. Commonly associated with calming, soothing effects." },
  humulene:       { color: "#c05621", aroma: "Hoppy, woody",      desc: "Earthy, hoppy and woody, also found in hops. Often paired with caryophyllene." },
  terpinolene:    { color: "#0891b2", aroma: "Fresh, herbal",     desc: "Fresh, herbal and slightly floral. Common in sativa-leaning strains." },
  ocimene:        { color: "#db2777", aroma: "Sweet, herbal",     desc: "Sweet, herbal and woodsy. Found in mint, parsley and orchids." },
  bisabolol:      { color: "#a16207", aroma: "Floral, soothing",  desc: "Delicate floral aroma found in chamomile. Associated with soothing effects." },
  farnesene:      { color: "#65a30d", aroma: "Apple, herbal",     desc: "Green apple and herbal notes. Also found in chamomile." },
  guaiol:         { color: "#0e7490", aroma: "Piney, woody",      desc: "Piney and woody with a hint of rose. Less common; earthy character." },
  nerolidol:      { color: "#7c2d12", aroma: "Woody, floral",     desc: "Woody, floral bark aroma found in jasmine and tea tree." },
  camphene:       { color: "#4b5563", aroma: "Damp, earthy",      desc: "Damp, earthy and fir-needle-like. Often present in small amounts." },
  valencene:      { color: "#ea580c", aroma: "Citrus, sweet",     desc: "Sweet citrus aroma named for Valencia oranges." },
  eucalyptol:     { color: "#0d9488", aroma: "Minty, cool",       desc: "Cool, minty and camphor-like, the main compound in eucalyptus." },
  fenchol:        { color: "#7c3aed", aroma: "Earthy, camphor",   desc: "Earthy, camphor and lemon notes, also found in basil." },
  borneol:        { color: "#334155", aroma: "Herbal, minty",     desc: "Herbal, minty and camphor-like, found in rosemary." },
}

function metaFor(name: string): TerpMeta {
  const key = name.toLowerCase().replace(/[^a-z]/g, "");
  for (const [k, v] of Object.entries(TERP_META)) {
    if (key.includes(k)) return v;
  }
  return { color: "#9ca3af", aroma: "" };
}

function prettyName(name: string) {
  // "Beta Caryophyllene" -> "β-Caryophyllene", "Alpha Pinene" -> "α-Pinene"
  return name
    .replace(/\bbeta\b/i, "β")
    .replace(/\balpha\b/i, "α")
    .replace(/^([βα])\s+/, "$1-");
}

interface Entry {
  name: string;
  value: number;
  unit?: string;
}

interface Props {
  /** Structured lab data with real % values (preferred). */
  profile?: Entry[];
  /** Fallback: just names, no values (parsed from description). */
  terpenes?: string[];
  size?: number;
}

/**
 * Terpene visualizer. When we have real % values (Dutchie structured data), it
 * renders a proportional donut + a ranked bar list with percentages, matching
 * Dutchie's own product page. Falls back to an even-weighted donut + simple
 * legend when only names are available.
 */
export default function TerpeneWheel({ profile, terpenes, size = 200 }: Props) {
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const entries: Entry[] = useMemo(() => {
    if (profile && profile.length > 0) return profile.slice(0, 8);
    if (terpenes && terpenes.length > 0) {
      // No values — even weight so the donut still reads.
      return terpenes.slice(0, 8).map((name) => ({ name, value: 1 }));
    }
    return [];
  }, [profile, terpenes]);

  const hasValues = !!(profile && profile.length > 0);

  if (entries.length === 0) return null;

  const total = entries.reduce((s, e) => s + e.value, 0) || 1;
  const max = Math.max(...entries.map((e) => e.value));

  // Donut geometry — proportional arcs when we have values.
  const radius = size / 2;
  const inner = radius * 0.6;
  const cx = radius;
  const cy = radius;
  const gap = 0.02;
  let acc = -Math.PI / 2;
  const arcs = entries.map((e) => {
    const frac = e.value / total;
    const start = acc + gap;
    const end = acc + frac * Math.PI * 2 - gap;
    acc += frac * Math.PI * 2;
    const x1 = cx + radius * Math.cos(start), y1 = cy + radius * Math.sin(start);
    const x2 = cx + radius * Math.cos(end), y2 = cy + radius * Math.sin(end);
    const x3 = cx + inner * Math.cos(end), y3 = cy + inner * Math.sin(end);
    const x4 = cx + inner * Math.cos(start), y4 = cy + inner * Math.sin(start);
    const large = end - start > Math.PI ? 1 : 0;
    return {
      color: metaFor(e.name).color,
      d: `M ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${inner} ${inner} 0 ${large} 0 ${x4} ${y4} Z`,
    };
  });

  const dominant = entries[0];
  const totalPct = hasValues ? total : null;
  // Which terpene's info card is open (index into entries), or null.
  const selected = selectedIdx !== null ? entries[selectedIdx] : null;
  const selectedMeta = selected ? metaFor(selected.name) : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500">
          Terpene profile
        </p>
        {totalPct !== null && (
          <span className="text-[11px] font-semibold text-gray-500">
            Total {totalPct.toFixed(2)}%
          </span>
        )}
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-5">
        {/* Donut — segments are clickable */}
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
          {arcs.map((a, i) => (
            <path
              key={i}
              d={a.d}
              fill={a.color}
              opacity={selectedIdx === null || selectedIdx === i ? 0.9 : 0.35}
              style={{ cursor: "pointer" }}
              onClick={() => setSelectedIdx(selectedIdx === i ? null : i)}
            />
          ))}
          <circle cx={cx} cy={cy} r={inner - 2} fill="white" />
          <text x={cx} y={cy - 4} textAnchor="middle" className="fill-gray-900"
                style={{ fontSize: size * 0.085, fontWeight: 700 }}>
            {prettyName((selected || dominant).name).replace(/^[βα]-/, "")}
          </text>
          <text x={cx} y={cy + size * 0.06} textAnchor="middle" className="fill-gray-500"
                style={{ fontSize: size * 0.05, fontWeight: 500 }}>
            {hasValues ? `${(selected || dominant).value.toFixed(2)}%` : "Dominant"}
          </text>
        </svg>

        {/* Ranked bar list — rows are clickable */}
        <div className="flex-1 min-w-0 w-full space-y-2">
          {entries.map((e, i) => {
            const meta = metaFor(e.name);
            const barPct = hasValues ? (e.value / max) * 100 : 100 / (i + 1);
            const isSel = selectedIdx === i;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setSelectedIdx(isSel ? null : i)}
                className={`w-full text-left text-sm rounded-md px-2 py-1 -mx-2 transition-colors ${isSel ? "bg-gray-50" : "hover:bg-gray-50"}`}
                aria-expanded={isSel}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ background: meta.color }} aria-hidden />
                    <span className={`font-semibold truncate ${isSel ? "text-gray-900" : "text-gray-800"}`}>{prettyName(e.name)}</span>
                  </span>
                  {hasValues && (
                    <span className="font-mono text-xs text-gray-600 tabular-nums shrink-0">
                      {e.value.toFixed(2)}%
                    </span>
                  )}
                </div>
                {hasValues && (
                  <div className="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${barPct}%`, background: meta.color }} />
                  </div>
                )}
                {!hasValues && meta.aroma && (
                  <span className="text-gray-500 text-xs">{meta.aroma}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Info card for the tapped terpene — like Dutchie's panel */}
      {selected && selectedMeta && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 relative">
          <button
            type="button"
            onClick={() => setSelectedIdx(null)}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-block w-3 h-3 rounded-full" style={{ background: selectedMeta.color }} aria-hidden />
            <h4 className="font-bold text-gray-900">{prettyName(selected.name)}</h4>
            {hasValues && <span className="text-xs font-mono text-gray-500">{selected.value.toFixed(2)}%</span>}
          </div>
          {selectedMeta.desc ? (
            <p className="text-sm text-gray-600 leading-relaxed pr-6">{selectedMeta.desc}</p>
          ) : (
            <p className="text-sm text-gray-500 italic">Aroma and effect data not available for this terpene.</p>
          )}
          {selectedMeta.aroma && (
            <p className="mt-2 text-xs uppercase tracking-wider text-gray-500">
              <span className="font-semibold">Aromas</span> · {selectedMeta.aroma}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Ranked cannabinoid list (THC, CBD, CBG…) with real % values — mirrors
 * Dutchie's "Cannabinoids" section. Simple bars, no donut.
 */
export function CannabinoidList({ profile }: { profile?: Entry[] }) {
  if (!profile || profile.length === 0) return null;
  const list = profile.slice(0, 8);
  const max = Math.max(...list.map((e) => e.value));
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-3">
        Cannabinoids
      </p>
      <div className="space-y-2">
        {list.map((e, i) => (
          <div key={i} className="text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-gray-800">{e.name}</span>
              <span className="font-mono text-xs text-gray-600 tabular-nums">
                {e.value.toFixed(2)}{e.unit || "%"}
              </span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full rounded-full bg-primary_green" style={{ width: `${(e.value / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Pull a terpene name list out of Dutchie's free-form description text, for
// products that lack structured lab data.
export function parseTerpenesFromDescription(description?: string | null): string[] {
  if (!description) return [];
  const match = description.match(/terpenes?:\s*([^\n]+)/i);
  if (!match) return [];
  return match[1]
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 8);
}
